import React, { ComponentProps, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { isArray, last, truncate, uniqBy } from 'lodash';
import { ParentSize } from '@visx/responsive';
import { AxisBottom } from '@visx/axis';
import { scaleBand } from '@visx/scale';
import classNames from 'classnames';

import { ScaleBand } from 'd3-scale';

import { Grouping } from '@/explore/types';
import { NoMatchBanner } from '@/components/banner';
import { DropdownMenuItem } from '@/components/dropdown';
import { GroupFilter } from '@/explore/utils/drilldown';

import {
  flattenGroupedChartData,
  getChartCount,
  getLeftRightAxisTypes,
  getSeries,
} from '../grouped-chart/utils';
import { GroupedChartData, isGroupedTimeSeriesData } from '../grouped-chart/types';
import { GroupedChart } from '../grouped-chart';
import { NumYTicks } from '../timeseries-chart/timeseries-chart';
import { getLegendData } from '../legend/utils';
import { Legend } from '../legend';
import { CategoryData } from '../grouped-chart/types';
import { CategoryValue } from './types';
import { VerticalCategoryChart } from './vertical-category-chart';
import { StackedCategoryChart } from './stacked-category-chart';
import { GroupAxisHeight } from '../timeseries-chart/utils';
import { alignScale, getValueScale } from '../grouped-chart/utils/scale';
import { getMaxLabelWidth } from '../utils';

import commonStyles, {
  multiChartChartHeight,
  minChartHeight,
  widthPerGroup,
  fontFamily,
} from '../charts.module.scss';

const MinChartHeight = parseInt(minChartHeight);
const MinChartWithSubAxisHeight = 400;
const WidthPerGroup = parseInt(widthPerGroup);
const YValuePaddingCoef = 0.2;
const HorizontalPadding = 20;
const VerticalPadding = 10;
const CategoryTickLabelMaxLength = 28;
const CategoryAxisDefaultHeight = 25;

export interface ChartColor {
  fill: string;
  highlight: string;
}

const getCategories = (data: GroupedChartData<CategoryData>): CategoryValue[] =>
  isArray(data)
    ? data.flatMap((item) => getCategories(item.items))
    : data.chartData.items.map((item) => item.categoryValue);

interface GroupedCategoryChartProps {
  data: GroupedChartData<CategoryData>;
  grouping: Grouping[];
  stacked?: boolean;
  showTotals: boolean;
  onCategoryClick?: (params: {
    x: number;
    y: number;
    key: string;
    groupFilters: GroupFilter[];
  }) => void;
  hideGrid?: boolean;
  timezone: string;
  isResized?: boolean;
  isTableVisible?: boolean;
  buildSeriesMenu?: (seriesKey: string) => DropdownMenuItem[];
}

export const GroupedCategoryChart = (props: GroupedCategoryChartProps) => {
  const { data, grouping, isResized = false, isTableVisible = true } = props;

  const categories = useMemo(() => uniqBy(getCategories(data), 'raw'), [data]);
  if (categories.length === 0) {
    return <NoMatchBanner />;
  }

  const customHeightEnabled = !isTableVisible && isResized;
  const chartCount = getChartCount(data);

  const isGroupedTimeSeries = flattenGroupedChartData(data).some((chartData) =>
    isGroupedTimeSeriesData(chartData),
  );
  const seriesDepth = isGroupedTimeSeries ? 3 : 2;
  const chartHeight = isGroupedTimeSeries
    ? MinChartWithSubAxisHeight
    : parseInt(multiChartChartHeight);
  const numChartGroups = Math.max(0, grouping.length - seriesDepth);
  const isMultiChart = numChartGroups > 0;

  return (
    <ParentSize
      debounceTime={0}
      className={classNames(commonStyles.chartContainer, {
        [commonStyles.customHeight]: customHeightEnabled,
      })}
      style={{
        minHeight:
          isMultiChart && !customHeightEnabled ? `${chartHeight * chartCount}px` : undefined,
      }}>
      {(parent) => <GroupedCategoryChartInner {...props} parent={parent} categories={categories} />}
    </ParentSize>
  );
};
const GroupedCategoryChartInner = (
  props: GroupedCategoryChartProps & {
    parent: { width: number; height: number };
    categories: CategoryValue[];
  },
) => {
  const {
    data,
    grouping,
    stacked = false,
    onCategoryClick,
    hideGrid = false,
    timezone,
    buildSeriesMenu,
    parent,
    showTotals,
    categories,
  } = props;

  const [highlightedCategory, setHighlightedCategory] = useState<CategoryValue | null>(null);
  const [highlightedSeriesKey, setHighlightedSeriesKey] = useState<string | null>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const [measuredFooterHeight, setMeasuredFooterHeight] = useState<number | null>(null);

  const series = getSeries(data);

  const groupKey = last(grouping)?.key;
  const containsBarChartSeries = series.some(({ chartType }) => chartType === 'bar');
  const chartCount = getChartCount(data);

  const isGroupedTimeSeries = flattenGroupedChartData(data).some((chartData) =>
    isGroupedTimeSeriesData(chartData),
  );
  const seriesDepth = isGroupedTimeSeries ? 3 : 2;
  const numChartGroups = Math.max(0, grouping.length - seriesDepth);
  const isMultiChart = numChartGroups > 0;

  const categoryScale = useMemo(
    () =>
      scaleBand({
        domain: categories.map((d) => d.formatted),
        range: [0, parent.width - WidthPerGroup * numChartGroups],
        paddingInner: containsBarChartSeries ? 0.2 : 0,
        paddingOuter: containsBarChartSeries ? 0.4 : 0.1,
      }),
    [categories, numChartGroups, parent.width, containsBarChartSeries],
  );

  useLayoutEffect(() => {
    if (footerRef.current) {
      const height = footerRef.current.clientHeight;
      setMeasuredFooterHeight(height);
    }
  }, [hideGrid, isGroupedTimeSeries, categories.length, series.length, parent.height]);

  const footerHeight =
    (measuredFooterHeight ?? 55) +
    (hideGrid ? 10 : 0) +
    (isGroupedTimeSeries ? GroupAxisHeight : 0);

  const chartHeight = Math.max(
    MinChartHeight,
    Math.floor(
      (parent.height -
        (footerHeight + (isMultiChart && isGroupedTimeSeries ? GroupAxisHeight : 0))) /
        chartCount,
    ),
  );

  const axisTypes = getLeftRightAxisTypes(series);

  const leftValueScale = useMemo(
    () =>
      getValueScale(
        data,
        stacked,
        'left',
        YValuePaddingCoef,
        [chartHeight, 0],
        NumYTicks,
        axisTypes.left === 'Integer' ? 1 : undefined,
      ),
    [axisTypes.left, chartHeight, data, stacked],
  );
  const rightValueScale = useMemo(
    () =>
      alignScale(
        getValueScale(
          data,
          stacked,
          'right',
          YValuePaddingCoef,
          [chartHeight, 0],
          NumYTicks,
          axisTypes.right === 'Integer' ? 1 : undefined,
        ),
        leftValueScale,
        NumYTicks,
      ),
    [axisTypes.right, chartHeight, data, leftValueScale, stacked],
  );
  const valueScales = useMemo(
    () => ({
      left: leftValueScale,
      right: rightValueScale,
    }),
    [leftValueScale, rightValueScale],
  );

  if (categories.length === 0) {
    return <NoMatchBanner />;
  }

  const BottomAxisComponent = getBottomAxisComponent(categoryScale);

  return (
    <div>
      <GroupedChart
        width={parent.width}
        widthPerGroup={WidthPerGroup}
        data={data}
        renderChart={(chartData, chartWidth, groupFilters) => {
          if (stacked) {
            const chartProps: ComponentProps<typeof StackedCategoryChart> = {
              data: chartData,
              categories,
              categoryScale,
              valueScales,
              width: chartWidth,
              height: chartHeight,
              hideGrid,
              groupKey,
              showTotals,
              highlightedCategory,
              highlightedSeriesKey,
              onCategoryHighlight: setHighlightedCategory,
              onCategoryClick: (input) =>
                onCategoryClick?.({
                  ...input,
                  groupFilters: [...groupFilters, ...input.groupFilters],
                }),
              timezone,
            };
            return <StackedCategoryChart {...chartProps} />;
          }

          const chartProps: ComponentProps<typeof VerticalCategoryChart> = {
            data: chartData,
            categories,
            categoryScale,
            valueScales,
            width: chartWidth,
            height: chartHeight,
            hideGrid,
            groupKey,
            highlightedCategory,
            highlightedSeriesKey,
            onCategoryHighlight: setHighlightedCategory,
            onCategoryClick: (input) =>
              onCategoryClick?.({
                ...input,
                groupFilters: [...groupFilters, ...input.groupFilters],
              }),
            timezone,
          };
          return <VerticalCategoryChart {...chartProps} data={chartProps.data} />;
        }}
      />
      <div ref={footerRef}>
        {!hideGrid && (
          <BottomAxisComponent
            width={parent.width}
            left={Math.max(0, WidthPerGroup * numChartGroups)}
            scale={categoryScale}
          />
        )}
        <div
          style={{
            marginLeft: Math.max(HorizontalPadding, WidthPerGroup * numChartGroups),
            marginRight: HorizontalPadding,
            marginTop: hideGrid ? VerticalPadding : 0,
          }}>
          <div className={commonStyles.chartFooter}>
            <Legend
              data={getLegendData(data)}
              buildSeriesMenu={buildSeriesMenu}
              onHighlight={setHighlightedSeriesKey}
              onHighlightEnd={() => setHighlightedSeriesKey(null)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const CharWidth = 5.43; // Approximate width of a character in pixels for the tick label font

const getBottomAxisComponent = (scale: ScaleBand<string>) => {
  const widestDomainValue = Math.max(...scale.domain().map(({ length }) => length)) * CharWidth;
  return scale.bandwidth() > 0 && widestDomainValue > scale.bandwidth()
    ? AngeledAxisBottom
    : HorizontalAxisBottom;
};

const HorizontalAxisBottom = (props: { width: number; left: number; scale: ScaleBand<string> }) => (
  <svg width={props.width} height={CategoryAxisDefaultHeight}>
    <AxisBottom
      left={props.left}
      scale={props.scale}
      hideAxisLine
      tickValues={props.scale.domain()}
      tickClassName={commonStyles.tickLabel}
      tickLabelProps={() => ({
        textAnchor: 'middle',
        dy: -4,
      })}
      rangePadding={300}
    />
  </svg>
);

const AngeledAxisBottom = (props: { width: number; left: number; scale: ScaleBand<string> }) => {
  const labels = props.scale
    .domain()
    .map((label) => truncate(label, { length: CategoryTickLabelMaxLength }));
  const maxWidth = getMaxLabelWidth(labels, `8px ${fontFamily}`);
  const height = Math.max(Math.ceil(maxWidth * 0.8), CategoryAxisDefaultHeight);
  return (
    <svg width={props.width} height={height}>
      <AxisBottom
        left={props.left}
        scale={props.scale}
        hideAxisLine
        tickValues={props.scale.domain()}
        tickClassName={commonStyles.tickLabel}
        tickLength={2}
        tickComponent={({ formattedValue, x, y }) => (
          <text x={x} y={y} transform={`rotate(30, ${x}, ${y})`}>
            {truncate(formattedValue, { length: CategoryTickLabelMaxLength })}
          </text>
        )}
      />
    </svg>
  );
};
